// @flow
import _keyBy from 'lodash/keyBy';
import {
  apiActionTypes as ActionType,
  apiTypes as Types,
  chainedApiActionTypes as chainedApiTypes,
  authActionTypes
} from '../constants';
import {
  deleteNodeAndAllChildren,
  deleteBranchesExceptOne,
  deleteNode
} from '../util/ApiUtils';

const initialState = {
  currentOrgId: 60,
  currentOrchestrationId: null,
  orchestrations: [],
  orchestrationsById: {},
  versions: {},
  versionsByOrchestrationId: {},
  creatives: [],
  creativesById: {},
  currentVersionId: null,
  isNewOrch: false,
  showErrors: false,
  authReady: false, // set to true after initially refreshing the access token
  userCountsByOrchestrationId: {},
  userCountsStatus: null,
  errorResponse: {
    message: ''
  }
};

// TODO: replace with keyBy
const array2ById = arr => arr.reduce((m, item) => ((m[item.id] = item), m), {}); // eslint-disable-line

export default function ApiReducer(
  state: Types.InitialState = initialState,
  action: Types.Action
) {
  switch (action.type) {
    case ActionType.ORCHS_REQUEST: {
      return {
        ...state,
        orchestrationsLoaded: false,
        orchestrationsError: false
      };
    }

    case ActionType.ORCHS_SUCCESS: {
      const { response: { orchestrations = [] } = {} } = action;
      const versionsByOrchestrationId = orchestrations.reduce(
        (o, orchestration) => {
          const { id, orchestration_versions } = orchestration;
          o[id] = array2ById(orchestration_versions); // eslint-disable-line
          return o;
        },
        {}
      );

      return {
        ...state,
        orchestrations,
        orchestrationsById: array2ById(orchestrations),
        versionsByOrchestrationId,
        orchestrationsLoaded: true
      };
    }

    case ActionType.USER_COUNTS_REQUEST: {
      const { userCountsError, ...sansError } = state;
      return {
        ...sansError,
        userCountsStatus: 'request'
      };
    }

    case ActionType.USER_COUNTS_SUCCESS: {
      return {
        ...state,
        userCountsStatus: 'success',
        userCountsByOrchestrationId: _keyBy(action.response, 'orchestration_id')
      };
    }

    case ActionType.CREATE_ORCH_SUCCESS: {
      const { orchestration } = action.payload;
      return {
        ...state,
        currentOrchestrationId: orchestration.id,
        orchestrationsById: {
          ...state.orchestrationsById,
          [orchestration.id]: orchestration
        }
      };
    }

    case ActionType.DELETE_ORCH_SUCCESS: {
      const { orchestrationId: deletedId } = action.payload;
      const {
        orchestrations,
        versionsByOrchestrationId,
        orchestrationsById
      } = state;
      delete orchestrationsById[deletedId];
      delete versionsByOrchestrationId[deletedId];
      return {
        ...state,
        orchestrations: orchestrations.filter(orchs => orchs.id !== deletedId),
        orchestrationsById,
        versionsByOrchestrationId
      };
    }

    case ActionType.ORCH_VERSIONS_SUCCESS: {
      const { orchestrationId } = action.payload;
      const { orchestration_versions } = action.response; // eslint-disable-line camelcase
      return {
        ...state,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [orchestrationId]: array2ById(orchestration_versions)
        }
      };
    }

    case ActionType.UPDATE_ORCH_VER_SUCCESS:
    case ActionType.ORCH_VERSION_SUCCESS: {
      const { versionId } = action.payload;
      const { orchestration_version: version } = action.response;
      const { orchestration_id: orchestrationId } = version;
      return {
        ...state,
        currentOrchestrationId: orchestrationId,
        ...(action.type === ActionType.UPDATE_ORCH_VER_SUCCESS && {
          isNewOrch: false
        }),
        currentVersionId: versionId,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [orchestrationId]: {
            ...state.versionsByOrchestrationId[orchestrationId],
            [versionId]: version
          }
        }
      };
    }

    case ActionType.CREATE_ORCH_VER_SUCCESS: {
      const { orchestration_version: version } = action.response;
      const { orchestration_id: orchestrationId } = version;
      const {
        [orchestrationId]: oldOrch,
        ...otherOrchs
      } = state.orchestrationsById;
      const updatedOrch = {
        ...oldOrch,
        orchestration_versions: oldOrch.orchestration_versions.concat([
          version.id
        ])
      };
      const newOrchestrations = state.orchestrations
        .filter(o => o.id !== orchestrationId)
        .concat([updatedOrch]);
      return {
        ...state,
        currentVersionId: version.id,
        orchestrations: newOrchestrations,
        orchestrationsById: {
          ...otherOrchs,
          [orchestrationId]: updatedOrch
        },
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [orchestrationId]: {
            ...state.versionsByOrchestrationId[orchestrationId],
            [version.id]: version
          }
        }
      };
    }

    case ActionType.ORCH_VER_CREATIVES_SUCCESS: {
      const { creatives } = action.response;
      return {
        ...state,
        creatives
      };
    }

    case ActionType.CREATE_ORCH_VER_CREATIVE_SUCCESS: {
      const { id: creativeId } = action.response;
      return {
        ...state,
        creativesById: {
          ...state.creativesById,
          [creativeId]: action.response
        }
      };
    }

    case ActionType.UPDATE_ORCH_VER_STATE_SUCCESS: {
      const { orchestrationId, versionId, versionState } = action.payload;
      const orchestrationVersionById =
        state.versionsByOrchestrationId[orchestrationId];
      return {
        ...state,
        isNewOrch: false,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [orchestrationId]: {
            ...orchestrationVersionById,
            [versionId]: {
              ...orchestrationVersionById[versionId],
              state: versionState
            }
          }
        }
      };
    }

    case ActionType.USER_COUNTS_FAILURE: {
      const { message } = action;
      return {
        userCountsError: { message },
        userCountsStatus: 'failure'
      };
    }
    case ActionType.ORCHS_FAILURE: {
      const { message } = action;

      if (action.status === 401) {
        // unauthorized, show permission denied page
        return {
          ...state,
          unauthorized: true
        };
      }

      // a different error
      return {
        ...state,
        orchestrationsError: true,
        showErrors: true,
        errorResponse: { message }
      };
    }

    case ActionType.CREATE_ORCH_FAILURE:
    case ActionType.UPDATE_ORCH_FAILURE:
    case ActionType.DELETE_ORCH_FAILURE:
    case ActionType.CREATE_ORCH_VER_FAILURE:
    case ActionType.UPDATE_ORCH_VER_FAILURE:
    case ActionType.ORCH_VERSION_FAILURE:
    case ActionType.CREATE_ORCH_VER_CREATIVE_FAILURE:
    case ActionType.UPDATE_ORCH_VER_STATE_FAILURE: {
      const { response: { message } = {} } = action;
      return {
        ...state,
        showErrors: true,
        errorResponse: { message }
      };
    }

    case ActionType.HIDE_ERRORS: {
      return {
        ...state,
        showErrors: false
      };
    }

    case ActionType.DELETE_ORCH_VER_NODE: {
      const { versionId, nodeName } = action.payload;
      const version =
        state.versionsByOrchestrationId[state.currentOrchestrationId][
          versionId
        ];
      const newNodes = deleteNode(version.nodes, nodeName);
      return {
        ...state,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [state.currentOrchestrationId]: {
            ...state.versionsByOrchestrationId[state.currentOrchestrationId],
            [versionId]: {
              ...version,
              nodes: newNodes
            }
          }
        }
      };
    }

    case ActionType.DELETE_ORCH_VER_ALL_EXCEPT_BRANCH: {
      const { versionId, nodeName, branchNameToKeep } = action.payload;
      const version =
        state.versionsByOrchestrationId[state.currentOrchestrationId][
          versionId
        ];
      const newNodes = deleteBranchesExceptOne(
        version.nodes,
        nodeName,
        branchNameToKeep
      );
      return {
        ...state,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [state.currentOrchestrationId]: {
            ...state.versionsByOrchestrationId[state.currentOrchestrationId],
            [versionId]: {
              ...version,
              nodes: newNodes
            }
          }
        }
      };
    }

    case ActionType.DELETE_ORCH_VER_ALL_CHILDREN: {
      const { versionId, nodeName } = action.payload;
      const version =
        state.versionsByOrchestrationId[state.currentOrchestrationId][
          versionId
        ];
      const newNodes = deleteNodeAndAllChildren(version.nodes, nodeName);
      return {
        ...state,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [state.currentOrchestrationId]: {
            ...state.versionsByOrchestrationId[state.currentOrchestrationId],
            [versionId]: {
              ...version,
              nodes: newNodes
            }
          }
        }
      };
    }
    case chainedApiTypes.CREATE_NEW_ORCH_REQUEST:
      return {
        ...state,
        isCreatingNewOrch: true,
        isCreatingNewOrchError: false
      };

    case chainedApiTypes.CREATE_NEW_ORCH_FAILURE: {
      const { error } = action;
      return {
        ...state,
        showErrors: true,
        errorResponse: { message: error },
        isCreatingNewOrch: false,
        isCreatingNewOrchError: { error: action.payload.error }
      };
    }

    case chainedApiTypes.CREATE_NEW_ORCH_SUCCESS: {
      const { version, orchestration } = action.payload;
      const { id: versionId, orchestration_id: orchestrationId } = version;
      return {
        ...state,
        isCreatingNewOrch: false,
        currentVersionId: versionId,
        currentOrchestrationId: orchestrationId,
        isNewOrch: true,
        orchestrationsById: {
          ...state.orchestrationsById,
          [orchestrationId]: {
            ...orchestration,
            orchestration_versions: [versionId]
          }
        },
        versionsByOrchestrationId: {
          [orchestrationId]: {
            [versionId]: action.payload.version
          }
        }
      };
    }

    case ActionType.UPDATE_ORCH_NAME_SUCCESS:
      return {
        ...state,
        orchestrationsById: {
          ...state.orchestrationsById,
          [action.response.orchestration.id]: {
            ...action.response.orchestration
          }
        }
      };

    case ActionType.UPDATE_ORCH_EVENT_SUCCESS: {
      const { orchestration_version: orchestrationVersion } = action.response;
      return {
        ...state,
        versionsByOrchestrationId: {
          ...state.versionsByOrchestrationId,
          [orchestrationVersion.orchestration_id]: {
            ...state.versionsByOrchestrationId[
              orchestrationVersion.orchestration_id
            ],
            [orchestrationVersion.id]: {
              ...orchestrationVersion
            }
          }
        }
      };
    }

    case ActionType.UPDATE_ORCH_LIMIT_SUCCESS:
      return {
        ...state,
        orchestrationsById: {
          ...state.orchestrationsById,
          [action.response.orchestration.id]: {
            ...action.response.orchestration
          }
        }
      };

    case authActionTypes.REFRESH_SUCCESS:
      return {
        ...state,
        authReady: true
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/api.js